import { CommonModule } from '@angular/common';
import { Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { formatDate, formatTime } from '@app/helpers/formatDateTime.helper';
import { createKeyedLoader } from '@app/helpers/loader';
import { connectionToStatus } from '@app/helpers/status.helper';
import { FileInfo } from '@app/models/document.model';
import { LegalEntityWithUser } from '@app/models/project.models';
import { LegalEntityService } from '@app/services/admin/legal-entity.service';
import { DownloadService } from '@app/services/file/download.service';
import { ToastService } from '@app/services/toast.service';
import { DecisionCellRendererComponent } from '@app/ui-components/grid-table/decision-cell-renderer/decision-cell-renderer.component';
import { DownloadCellRendererComponent } from '@app/ui-components/grid-table/download-cell-renderer/download-cell-renderer.component';
import { customRendererCellStyle } from '@app/ui-components/grid-table/custom-renderer-cell-style';
import { GridTableComponent } from '@app/ui-components/grid-table/grid-table.component';
import { TwoLineCellRendererComponent } from '@app/ui-components/grid-table/two-line-cell-renderer/two-line-cell-renderer.component';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
  ColDef,
  ICellRendererParams,
  IDatasource,
  IGetRowsParams,
} from 'ag-grid-community';
import { finalize, map } from 'rxjs';

type Row = {
  id: string;
  createdAt: string;
  createdDate: string;
  createdTime: string;
  legalEntity: string;
  title: string;
  subtitle: string;
  documents: string;
  docStatus?: 'pending' | 'approved' | 'rejected';
  enabledConnection?: boolean | null;
  legalEntityId: string;
  userTitle: string;
  userSubtitle: string;
};

@Component({
  selector: 'app-approval-requests',
  standalone: true,
  imports: [CommonModule, GridTableComponent, TranslatePipe],
  templateUrl: './approval-requests.component.html',
  styleUrl: './approval-requests.component.scss',
})
export class ApprovalRequestsComponent implements OnInit {
  private readonly legalEntityService = inject(LegalEntityService);
  private readonly downloadService = inject(DownloadService);
  private readonly translate = inject(TranslateService);
  private readonly toast = inject(ToastService);
  private readonly destroyRef = inject(DestroyRef);

  columns: ColDef[] = [];
  rowModelType: 'infinite' = 'infinite';
  cacheBlockSize = 10;
  maxBlocksInCache = 5;
  rowHeight = 56;
  datasource!: IDatasource;

  readonly searchTerm = signal<string>('');
  readonly loading = signal<boolean>(false);
  private readonly rowLoader = createKeyedLoader();

  ngOnInit(): void {
    this.buildColumns();

    this.translate.onLangChange
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.buildColumns());

    this.datasource = this.buildDatasource();
  }

  onSearch(term: string | undefined): void {
    this.searchTerm.set((term ?? '').trim());
    this.datasource = this.buildDatasource();
  }

  private buildColumns(): void {
    this.columns = [
      {
        field: 'createdAt',
        headerName: this.translate.instant('admin.users.table.date'),
        cellRenderer: TwoLineCellRendererComponent,
        cellRendererParams: (param: ICellRendererParams<Row>) => ({
          title: param.data?.createdDate ?? '-',
          subtitle: param.data?.createdTime ?? '',
        }),
        cellStyle: customRendererCellStyle,
        width: 100,
        flex: 0,
        suppressNavigable: true,
        editable: false,
        sortable: false,
        filter: false,
      },
      {
        field: 'user',
        headerName:
          this.translate.instant('admin.legalEntities.table.user') || 'User',
        cellRenderer: TwoLineCellRendererComponent,
        cellRendererParams: (param: ICellRendererParams<Row>) => ({
          title: param.data?.userTitle ?? '',
          subtitle: param.data?.userSubtitle ?? '',
        }),
        cellStyle: customRendererCellStyle,
        width: 250,
        flex: 0,
        suppressNavigable: true,
        editable: false,
        sortable: false,
        filter: false,
      },
      {
        field: 'legalEntity',
        headerName: this.translate.instant('admin.users.table.legalEntity'),
        cellRenderer: TwoLineCellRendererComponent,
        cellRendererParams: (param: ICellRendererParams<Row>) => ({
          title: param.data?.title ?? '',
          subtitle: param.data?.subtitle ?? '',
        }),
        cellStyle: customRendererCellStyle,
        editable: false,
        sortable: false,
        filter: false,
      },
      {
        field: 'documents',
        headerName: this.translate.instant('admin.users.table.documents'),
        cellRenderer: DownloadCellRendererComponent,
        cellRendererParams: (param: ICellRendererParams<Row>) => {
          const rowId = param.data?.id ?? null;

          return {
            actions: {
              fetchList: () =>
                this.rowLoader.trackInProgress(rowId)(
                  this.legalEntityService
                    .getApprovalRequestFilesForEntity(param.data!.legalEntityId)
                    .pipe(map((files: FileInfo[]) => files))
                ),
              download: (fileId: string) =>
                this.rowLoader.trackInProgress(rowId)(
                  this.downloadService.downloadFileById(fileId)
                ),
              loading: () => this.rowLoader.isInProgress(rowId),
            },
          };
        },
        maxWidth: 140,
        suppressNavigable: true,
        editable: false,
        sortable: false,
        filter: false,
        cellClass: 'text-center',
      },
      {
        field: 'decision',
        headerName: '',
        cellRenderer: DecisionCellRendererComponent,
        cellRendererParams: (param: ICellRendererParams<Row>) => ({
          status: connectionToStatus(param.data?.enabledConnection ?? null),
          actions: {
            approve: (rowId: string) =>
              this.legalEntityService.updateApprovalRequest(rowId, true),
            decline: (rowId: string) =>
              this.legalEntityService.updateApprovalRequest(rowId, false),
          },
        }),
        width: 150,
        flex: 0,
        suppressNavigable: true,
        editable: false,
        sortable: false,
        filter: false,
        cellStyle: {
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          textAlign: 'center',
          gap: '8px',
        },
        cellClass: 'text-center',
      },
    ];
  }

  private buildDatasource(): IDatasource {
    return {
      rowCount: undefined,
      getRows: (param: IGetRowsParams) => {
        this.loading.set(true);

        this.legalEntityService
          .getApprovableRequests()
          .pipe(finalize(() => this.loading.set(false)))
          .subscribe({
            next: (
              res: LegalEntityWithUser[] | { data?: LegalEntityWithUser[] }
            ) => {
              const list: LegalEntityWithUser[] = Array.isArray(res)
                ? res
                : res?.data ?? [];

              const rows: Row[] = list.map((item) => {
                const created = item.creationDate ?? '';
                const createdDate = formatDate(created);
                const createdTime = formatTime(created);

                const pd = item.user?.personalData ?? null;
                const nameParts = [pd?.firstName, pd?.midName, pd?.lastName]
                  .map((v) => (v ?? '').trim())
                  .filter(Boolean);
                const userTitle = nameParts.length
                  ? nameParts.join(' ')
                  : item.user?.userName ?? '-';
                const userSubtitle = (item.user?.email ?? pd?.email ?? '')
                  .toString()
                  .trim();

                const addr = item.legalEntity?.address ?? null;
                const part = (v?: string | null) =>
                  (v ?? '').toString().trim() || '';
                const subtitle = addr
                  ? [
                      part(addr.doorOrFloor),
                      part(addr.houseNumber),
                      [part(addr.publicSpace), part(addr.publicSpaceType)]
                        .filter(Boolean)
                        .join(' '),
                      part(addr.city),
                      part(addr.zipCode),
                      part(addr.countryCode),
                    ]
                      .filter(Boolean)
                      .join(', ')
                  : '';

                const title = [
                  part(item.legalEntity?.name),
                  item.legalEntity?.taxNumber
                    ? `(${part(item.legalEntity.taxNumber)})`
                    : '',
                ]
                  .filter(Boolean)
                  .join(' ');

                return {
                  id: item.id,
                  createdAt: created,
                  createdDate,
                  createdTime,
                  userTitle,
                  userSubtitle,
                  legalEntity: title || '-',
                  title: title || '-',
                  subtitle,
                  documents: 'View',
                  enabledConnection: item.enabledConnection ?? null,
                  legalEntityId: item.pczrtLegalEntityId,
                };
              });

              const term = this.searchTerm().toLowerCase();
              const filtered = term
                ? rows.filter((r) =>
                    (
                      r.userTitle +
                      ' ' +
                      r.userSubtitle +
                      ' ' +
                      r.legalEntity +
                      ' ' +
                      r.subtitle
                    )
                      .toLowerCase()
                      .includes(term)
                  )
                : rows;

              const page = filtered.slice(param.startRow, param.endRow);
              param.successCallback(page, filtered.length);
            },
            error: () => {
              this.toast.error(
                this.translate.instant('admin.toasts.tableLoadFailed')
              );
              param.successCallback([], 0);
            },
          });
      },
    };
  }
}
